import axios, { AxiosResponse } from 'axios';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from 'api/use-api-mutation';
import { API_NAMES } from 'api/constants';
import {
    AddPeriodTemplateTagTypeRequest,
    CreatePeriodTemplateRequest,
    PeriodsApi,
    PeriodTemplate,
    PeriodTemplateAllowedTagType,
    PeriodTemplatesApi,
    UpdatePeriodTemplateRequest,
} from 'oas/internal';

export interface PeriodTagRef {
    tag_name: string;
    tag_type_name: string;
}

export interface ApplyPeriodTagsDiff {
    periodId: string;
    activityId: string;
    toAdd: PeriodTagRef[];
    toRemove: PeriodTagRef[];
}

export const useAddPeriodTemplateAllowedTagType = (): UseMutationResult<
    AxiosResponse<PeriodTemplateAllowedTagType>,
    unknown,
    AddPeriodTemplateTagTypeRequest
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (request, apiConfig, axiosInstance) => {
            return await new PeriodTemplatesApi(apiConfig, '', axiosInstance).addPeriodTemplateAllowedTagType(request);
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['period-templates', 'allowed-tag-types'] });
            },
        },
        ['period-templates', 'allowed-tag-types'],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useDeletePeriodTemplateAllowedTagType = (): UseMutationResult<AxiosResponse<void>, unknown, string> => {
    const queryClient = useQueryClient();
    return useApiMutation(
        async (tagTypeId, apiConfig, axiosInstance) => {
            return await new PeriodTemplatesApi(apiConfig, '', axiosInstance).deletePeriodTemplateAllowedTagType(
                tagTypeId,
            );
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['period-templates', 'allowed-tag-types'] });
                queryClient.invalidateQueries({ queryKey: ['period-templates'] });
            },
        },
        ['period-templates', 'allowed-tag-types'],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useCreatePeriodTemplate = (): UseMutationResult<PeriodTemplate, unknown, CreatePeriodTemplateRequest> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (data, apiConfig, axiosInstance) => {
            // The shared response interceptor resolves failed requests with the AxiosError rather than
            // rejecting, so re-throw it here to surface failures through react-query's error path.
            const response = await new PeriodTemplatesApi(apiConfig, '', axiosInstance).createPeriodTemplate(data);

            if (axios.isAxiosError(response)) {
                throw response;
            }

            return response.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['period-templates'] });
            },
        },
        ['period-templates'],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useDeletePeriodTemplate = (): UseMutationResult<AxiosResponse<void>, unknown, string> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (id, apiConfig, axiosInstance) => {
            const response = await new PeriodTemplatesApi(apiConfig, '', axiosInstance).deletePeriodTemplates([{ id }]);

            if (axios.isAxiosError(response)) {
                throw response;
            }

            return response;
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['period-templates'] });
            },
        },
        ['period-templates'],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useUpdatePeriodTemplate = (): UseMutationResult<
    AxiosResponse<PeriodTemplate>,
    unknown,
    { id: string; data: UpdatePeriodTemplateRequest }
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async ({ id, data }, apiConfig, axiosInstance) => {
            return await new PeriodTemplatesApi(apiConfig, '', axiosInstance).updatePeriodTemplate(id, data);
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['period-templates'] });
            },
        },
        ['period-templates'],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useApplyPeriodTagsDiff = (): UseMutationResult<void, unknown, ApplyPeriodTagsDiff> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async ({ periodId, toAdd, toRemove }, apiConfig, axiosInstance) => {
            const api = new PeriodsApi(apiConfig, '', axiosInstance);

            // this is really not great depending on the number of tags being applied
            await Promise.all([
                ...toAdd.map((tag) => api.patchPeriodTags(periodId, tag)),
                ...toRemove.map((tag) => api.deletePeriodTags(periodId, tag)),
            ]);
        },
        {
            onSettled: (_data, _error, variables) => {
                queryClient.invalidateQueries({ queryKey: ['activities', 'periods'] });
                queryClient.invalidateQueries({ queryKey: ['activities', variables.activityId] });
                queryClient.invalidateQueries({ queryKey: ['activities', 'deep'] });
                queryClient.invalidateQueries({ queryKey: ['periods', variables.periodId, 'tags'] });
            },
        },
        ['periods', 'tags'],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};
